//
/* GS://PROTOCOLO
 * Arquivo: src/pages/admin/SystemConfigsPage.js
 * Versão: 1.9.1 – FIX: Vitrine Vertical Layout
 * Impacto: Ajusta exclusivamente o layout da Vitrine para Vertical. Mantém lógica e outros cards intactos.
 */

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc, setDoc, getDocs, collection, serverTimestamp } from 'firebase/firestore';
import { Save, AlertTriangle, Loader, CheckCircle, Shield, Calendar, Lock, FileText, Crown, Star, Users } from 'lucide-react';

export default function SystemConfigsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Estado da Vitrine
  const [options, setOptions] = useState([]);
  
  // Estado de Manutenção
  const [maintenance, setMaintenance] = useState({
    block_access: false,       
    block_message: '',         
    show_warning: false,       
    warning_message: '',       
    scheduled_date: ''         
  });

  // Estado dos Textos Legais
  const [legalDocs, setLegalDocs] = useState({
    terms: '', privacy: '', goldenTerms: '', aboutUs: '', pointsRules: '', clubRules: ''
  });

  // Estado dos Planos GoldenClub
  const [clubPlans, setClubPlans] = useState([]);

  const fixedFlags = {
    use_cloud_sku: true,
    use_cloud_wallet: true
  };

  useEffect(() => {
    fetchAllConfigs();
  }, []);

  const fetchAllConfigs = async () => {
    try {
      setLoading(true);
      
      // 1. Busca Vitrine
      const vitrineRef = doc(db, 'system_configs', 'recharge_options');
      const vitrineSnap = await getDoc(vitrineRef);
      if (vitrineSnap.exists() && vitrineSnap.data().options) {
        setOptions(vitrineSnap.data().options);
      } else {
        setOptions([
          { value: 29.90, pointsPix: 32, pointsCard: 30, recommended: false },
          { value: 59.90, pointsPix: 67, pointsCard: 64, recommended: true },
          { value: 119.90, pointsPix: 126, pointsCard: 123, recommended: false }
        ]);
      }

      // 2. Busca Configs Globais
      const configRef = doc(db, '_SYSTEM_CONFIG', 'main');
      const configSnap = await getDoc(configRef);
      if (configSnap.exists()) {
        const data = configSnap.data();
        const schedule = data.maintenanceSchedule || {};
        setMaintenance({
            block_access: data.featureFlags?.maintenance_mode || false,
            block_message: schedule.blockMessage || 'Sistema em manutenção crítica.',
            show_warning: schedule.active || false,
            warning_message: schedule.message || '',
            scheduled_date: schedule.scheduledDate || ''
        });
      }

      // 3. Busca Textos Legais
      const registryRef = collection(db, 'policy_registry');
      const [terms, privacy, gTerms, about, points, club] = await Promise.all([
        getDoc(doc(registryRef, 'terms_of_use')),
        getDoc(doc(registryRef, 'privacy_policy')),
        getDoc(doc(registryRef, 'goldenpass_terms')),
        getDoc(doc(registryRef, 'about_us')),
        getDoc(doc(registryRef, 'goldenpoints_rules')),
        getDoc(doc(registryRef, 'goldenclub_rules'))
      ]);
      setLegalDocs({
        terms: terms.exists() ? terms.data().content : '',
        privacy: privacy.exists() ? privacy.data().content : '',
        goldenTerms: gTerms.exists() ? gTerms.data().content : '',
        aboutUs: about.exists() ? about.data().content : '',
        pointsRules: points.exists() ? points.data().content : '',
        clubRules: club.exists() ? club.data().content : ''
      });

      // 4. Busca Planos do Clube
      const plansSnap = await getDocs(collection(db, 'club_plans'));
      if (!plansSnap.empty) {
        const loadedPlans = plansSnap.docs.map(d => ({ 
            id: d.id, 
            ...d.data(),
            active: d.data().active ?? true,
            benefits: d.data().benefits ? d.data().benefits.join(', ') : '' 
        }));
        setClubPlans(loadedPlans);
      } else {
        setClubPlans([
          { id: 'Bronze', name: 'Plano Bronze', price: 19.90, description: 'Plano inicial.', active: true, benefits: 'Frete Grátis, 5% OFF' },
          { id: 'GoldenSisters', name: 'GoldenSisters', price: 0, description: 'Acesso gratuito.', active: true, benefits: 'Acesso ao App' }
        ]);
      }

    } catch (error) {
      console.error("Erro ao buscar configs:", error);
      setMessage({ type: 'error', text: 'Erro ao carregar configurações.' });
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  };
  const handleRecommendedChange = (index) => {
    const newOptions = options.map((opt, i) => ({ ...opt, recommended: i === index }));
    setOptions(newOptions);
  };
  const handleMaintenanceChange = (field, value) => {
    setMaintenance(prev => ({ ...prev, [field]: value }));
  };
  const handleLegalChange = (field, value) => {
    setLegalDocs(prev => ({ ...prev, [field]: value }));
  };
  const handlePlanChange = (index, field, value) => {
    const newPlans = [...clubPlans];
    newPlans[index] = { ...newPlans[index], [field]: value };
    setClubPlans(newPlans);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      if (options.some(o => o.value <= 0)) throw new Error("Vitrine: Valores devem ser positivos.");
      if (maintenance.show_warning && !maintenance.warning_message) throw new Error("Aviso: Escreva uma mensagem para o banner.");
      if (maintenance.block_access && !maintenance.block_message) throw new Error("Bloqueio: Escreva uma mensagem para a tela de bloqueio.");
      
      // Validação permite 0, bloqueia apenas negativos
      if (clubPlans.some(p => p.price < 0)) throw new Error("Clube: Preços não podem ser negativos.");

      const batchPromises = [];

      // 1. Vitrine
      batchPromises.push(setDoc(doc(db, 'system_configs', 'recharge_options'), {
        options: options.map(o => ({
          value: Number(o.value),
          pointsPix: Number(o.pointsPix),
          pointsCard: Number(o.pointsCard),
          recommended: o.recommended
        })),
        updatedAt: serverTimestamp()
      }, { merge: true }));

      // 2. Configs Globais
      batchPromises.push(setDoc(doc(db, '_SYSTEM_CONFIG', 'main'), {
        featureFlags: { maintenance_mode: maintenance.block_access, ...fixedFlags },
        maintenanceSchedule: {
            active: maintenance.show_warning,
            message: maintenance.warning_message,
            blockMessage: maintenance.block_message,
            scheduledDate: maintenance.scheduled_date,
            updatedAt: serverTimestamp()
        },
        metadata: { updatedBy: 'admin_panel', lastUpdate: serverTimestamp() }
      }, { merge: true }));

      // 3. Textos Legais
      const legalMap = [
        { id: 'terms_of_use', content: legalDocs.terms, title: "Termos de Serviço" },
        { id: 'privacy_policy', content: legalDocs.privacy, title: "Política de Privacidade" },
        { id: 'goldenpass_terms', content: legalDocs.goldenTerms, title: "Regulamento GoldenPass" },
        { id: 'about_us', content: legalDocs.aboutUs, title: "Sobre Nós" },
        { id: 'goldenpoints_rules', content: legalDocs.pointsRules, title: "Regras GoldenPoints" },
        { id: 'goldenclub_rules', content: legalDocs.clubRules, title: "Regras GoldenClub" }
      ];
      legalMap.forEach(item => {
        batchPromises.push(setDoc(doc(db, 'policy_registry', item.id), {
          content: item.content || "", title: item.title, version: "1.2.0 (Admin)", updatedAt: serverTimestamp()
        }, { merge: true }));
      });

      // 4. Planos Clube
      clubPlans.forEach(plan => {
        const benefitsArray = typeof plan.benefits === 'string' 
            ? plan.benefits.split(',').map(s => s.trim()).filter(s => s.length > 0)
            : [];

        batchPromises.push(setDoc(doc(db, 'club_plans', plan.id), {
          name: plan.name || "Novo Plano",
          price: Number(plan.price),
          description: plan.description || "",
          active: plan.active !== undefined ? plan.active : true, 
          benefits: benefitsArray,
          updatedAt: serverTimestamp()
        }, { merge: true }));
      });

      await Promise.all(batchPromises);
      setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
    } catch (error) {
      console.error("Erro ao salvar:", error);
      setMessage({ type: 'error', text: error.message || 'Erro ao salvar.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-white p-10">Carregando...</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Configurações do Sistema</h1>
          <p className="text-gray-400">Controle de manutenção, vitrine e conteúdo legal.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-brand-gold text-brand-black font-bold rounded-lg hover:bg-yellow-500 disabled:opacity-50 transition-colors shadow-lg"
        >
          {saving ? <Loader className="animate-spin w-5 h-5 mr-2" /> : <Save className="w-5 h-5 mr-2" />}
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>

      {message && (
        <div className={`p-4 mb-6 rounded-lg flex items-center border ${message.type === 'error' ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-green-900/20 border-green-800 text-green-200'}`}>
          {message.type === 'error' ? <AlertTriangle className="mr-2" /> : <CheckCircle className="mr-2" />}
          {message.text}
        </div>
      )}

      {/* SEÇÃO 1: CENTRAL DE MANUTENÇÃO (Preservado) */}
      <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 mb-8 backdrop-blur-sm">
        <h2 className="text-xl font-semibold text-brand-gold mb-4 flex items-center border-b border-gray-700 pb-2">
          <Shield className="mr-2 w-5 h-5" /> Central de Manutenção Programada
        </h2>
        
        <div className="grid gap-6 md:grid-cols-2">
            
            {/* CARD 1: Aviso Prévio */}
            <div className={`p-5 rounded-lg border transition-all ${maintenance.show_warning ? 'bg-yellow-900/10 border-yellow-500' : 'bg-gray-800 border-gray-600'}`}>
                <div className="flex justify-between items-center mb-4">
                    <span className="font-bold text-gray-200 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-yellow-500"/> 1. Aviso Programado
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={maintenance.show_warning} onChange={(e) => handleMaintenanceChange('show_warning', e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-yellow-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                </div>
                
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs text-gray-400 mb-1">Mensagem do Banner (Aviso)</label>
                        <input 
                            type="text" 
                            placeholder="Ex: Atualização dia 15/01."
                            value={maintenance.warning_message}
                            onChange={(e) => handleMaintenanceChange('warning_message', e.target.value)}
                            disabled={!maintenance.show_warning && !maintenance.warning_message}
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-yellow-500 focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-400 mb-1">Previsão / Horário</label>
                        <input 
                            type="text" 
                            placeholder="Ex: Retorno previsto às 14h."
                            value={maintenance.scheduled_date}
                            onChange={(e) => handleMaintenanceChange('scheduled_date', e.target.value)}
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm focus:border-yellow-500 focus:outline-none"
                        />
                    </div>
                </div>
            </div>

            {/* CARD 2: Bloqueio Total */}
            <div className={`p-5 rounded-lg border transition-all ${maintenance.block_access ? 'bg-red-900/10 border-red-500' : 'bg-gray-800 border-gray-600'}`}>
                <div className="flex justify-between items-center mb-4">
                    <span className="font-bold text-gray-200 flex items-center">
                        <Lock className="w-5 h-5 mr-2 text-red-500"/> 2. Bloqueio Total
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={maintenance.block_access} onChange={(e) => handleMaintenanceChange('block_access', e.target.checked)} />
                        <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-red-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                </div>
                
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs text-red-300 mb-1 font-bold">Mensagem de Bloqueio (Tela Vermelha)</label>
                        <input 
                            type="text" 
                            placeholder="Ex: Estamos em manutenção crítica. Volte logo."
                            value={maintenance.block_message}
                            onChange={(e) => handleMaintenanceChange('block_message', e.target.value)}
                            disabled={!maintenance.block_access && !maintenance.block_message}
                            className="w-full bg-gray-900 border border-red-500/50 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                        />
                    </div>
                    
                    <div className="bg-red-900/20 p-3 rounded text-xs text-red-200 border border-red-900/50 mt-2">
                        <p className="font-bold mb-1">⚠️ AÇÃO CRÍTICA</p>
                        <p>Impede qualquer acesso ao App.</p>
                    </div>
                </div>
                
                <div className="mt-4 text-center">
                    <span className={`text-xs px-2 py-1 rounded font-bold ${maintenance.block_access ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-400'}`}>
                        STATUS: {maintenance.block_access ? 'BLOQUEADO' : 'LIBERADO'}
                    </span>
                </div>
            </div>
        </div>
      </div>

      {/* SEÇÃO 2: CONTEÚDO INSTITUCIONAL */}
      <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 mb-8 backdrop-blur-sm">
        <h2 className="text-xl font-semibold text-brand-gold mb-4 flex items-center border-b border-gray-700 pb-2">
          <FileText className="mr-2 w-5 h-5" /> Conteúdo Institucional e Legal
        </h2>
        
        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <label className="block text-sm text-gray-300 mb-2 font-bold">Termos de Serviço</label>
            <textarea rows={5} value={legalDocs.terms} onChange={(e) => handleLegalChange('terms', e.target.value)} className="w-full bg-gray-900 border border-gray-600 rounded p-3 text-white text-xs resize-y" placeholder="Texto..." />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2 font-bold">Política de Privacidade</label>
            <textarea rows={5} value={legalDocs.privacy} onChange={(e) => handleLegalChange('privacy', e.target.value)} className="w-full bg-gray-900 border border-gray-600 rounded p-3 text-white text-xs resize-y" placeholder="Texto..." />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2 font-bold">Sobre Nós (Institucional)</label>
            <textarea rows={5} value={legalDocs.aboutUs} onChange={(e) => handleLegalChange('aboutUs', e.target.value)} className="w-full bg-gray-900 border border-gray-600 rounded p-3 text-white text-xs resize-y" placeholder="Conte a história..." />
          </div>
          <div>
            <label className="block text-sm text-yellow-500 mb-2 font-bold flex items-center"><Star className="w-3 h-3 mr-1"/> Regras GoldenPoints</label>
            <textarea rows={5} value={legalDocs.pointsRules} onChange={(e) => handleLegalChange('pointsRules', e.target.value)} className="w-full bg-gray-900 border border-yellow-600/30 rounded p-3 text-white text-xs resize-y" placeholder="Regulamento de pontos..." />
          </div>
          <div>
            <label className="block text-sm text-purple-400 mb-2 font-bold flex items-center"><Crown className="w-3 h-3 mr-1"/> Regras GoldenClub</label>
            <textarea rows={5} value={legalDocs.clubRules} onChange={(e) => handleLegalChange('clubRules', e.target.value)} className="w-full bg-gray-900 border border-purple-600/30 rounded p-3 text-white text-xs resize-y" placeholder="Regulamento do Clube..." />
          </div>
          <div>
            <label className="block text-sm text-brand-gold mb-2 font-bold flex items-center"><Lock className="w-3 h-3 mr-1"/> Regulamento GoldenPass</label>
            <textarea rows={5} value={legalDocs.goldenTerms} onChange={(e) => handleLegalChange('goldenTerms', e.target.value)} className="w-full bg-gray-900 border border-brand-gold/30 rounded p-3 text-white text-xs resize-y" placeholder="Regras do Passe..." />
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
          {/* SEÇÃO 3: VITRINE - [FIX]: Alterado para grid-cols-1 (Vertical) */}
          <div className="bg-brand-dark p-6 rounded-xl border border-gray-700 mb-8">
            <h2 className="text-xl font-semibold text-brand-gold mb-4 flex items-center">
              <span className="mr-2">💎</span> Vitrine de Recargas GoldenPass
            </h2>
            <div className="grid gap-6 grid-cols-1">
              {options.map((opt, index) => (
                <div key={index} className={`p-4 rounded-lg border-2 transition-all ${opt.recommended ? 'border-brand-gold bg-brand-gold/10' : 'border-gray-700 bg-gray-800'}`}>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-bold text-gray-300">Opção {index + 1}</span>
                    <label className="flex items-center cursor-pointer">
                      <input type="radio" name="recommended" checked={opt.recommended} onChange={() => handleRecommendedChange(index)} className="mr-2 text-brand-gold focus:ring-brand-gold" />
                      <span className={`text-xs ${opt.recommended ? 'text-brand-gold font-bold' : 'text-gray-500'}`}>Recomendado</span>
                    </label>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Valor (R$)</label>
                      <input type="number" step="0.01" value={opt.value} onChange={(e) => handleOptionChange(index, 'value', e.target.value)} className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:border-brand-gold focus:outline-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Pts. Pix</label>
                        <input type="number" value={opt.pointsPix} onChange={(e) => handleOptionChange(index, 'pointsPix', e.target.value)} className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:border-brand-gold focus:outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Pts. Cartão</label>
                        <input type="number" value={opt.pointsCard} onChange={(e) => handleOptionChange(index, 'pointsCard', e.target.value)} className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:border-brand-gold focus:outline-none" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SEÇÃO 4: GESTÃO GOLDENCLUB */}
          <div className="bg-purple-900/10 p-6 rounded-xl border border-purple-900/50">
            <h2 className="text-xl font-semibold text-purple-400 mb-4 flex items-center">
              <Users className="mr-2 w-5 h-5" /> Planos GoldenClub
            </h2>
            <div className="space-y-4">
              {clubPlans.map((plan, index) => (
                <div key={plan.id} className="p-3 rounded border border-purple-800/30 bg-purple-900/20 flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                        <input 
                            type="text" value={plan.name} 
                            onChange={(e) => handlePlanChange(index, 'name', e.target.value)}
                            className="bg-transparent border-b border-purple-500 text-white font-bold text-sm w-1/2 focus:outline-none"
                        />
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">R$</span>
                            <input 
                                type="number" value={plan.price} 
                                onChange={(e) => handlePlanChange(index, 'price', e.target.value)}
                                className="bg-purple-900/50 border border-purple-600 rounded px-2 text-white text-sm w-20 text-right"
                            />
                        </div>
                    </div>
                    <textarea 
                        rows={2}
                        value={plan.description}
                        onChange={(e) => handlePlanChange(index, 'description', e.target.value)}
                        className="w-full bg-purple-900/30 border border-purple-700 rounded p-2 text-gray-300 text-xs resize-none"
                        placeholder="Descrição do plano..."
                    />
                    <input 
                        type="text" 
                        value={plan.benefits}
                        onChange={(e) => handlePlanChange(index, 'benefits', e.target.value)}
                        className="w-full bg-purple-900/30 border border-purple-700 rounded p-2 text-gray-300 text-xs"
                        placeholder="Benefícios (separados por vírgula)..."
                    />
                </div>
              ))}
            </div>
          </div>
      </div>
    </div>
  );
}